import { type CSSProperties, isValidElement } from 'react';

import classNames from 'classnames';

import { generatePositionStyle } from '@/utils/positioning';
import { getHeightStyle, getWidthStyle } from '@/utils/sizing';

import { getBackgroundColorStyle, getBackgroundDarkenStyle } from '../utils/background';
import { useAnimatedBlock } from '../utils/useAnimatedBlock';

import { BackgroundImage } from '../backgroundImage';
import { Link } from '../link';
import { CONTAINER_DEFAULTS } from './Container.constants';
import * as styles from './Container.css';
import type { ContainerElementProps } from './Container.type';

const POSITION_DEFAULTS = {
  position: 'relative',
  positionTop: '0px',
  positionRight: '0px',
  positionBottom: '0px',
  positionLeft: '0px',
  positionTopActive: true,
  positionRightActive: true,
  positionBottomActive: false,
  positionLeftActive: false,
  centerHorizontally: false,
  centerVertically: false,
  zIndex: 'auto',
};

const generateTransition = (transitionDuration: number | string, transition: string) => {
  const transitionDurationString =
    typeof transitionDuration === 'number' ? `${transitionDuration}s` : transitionDuration;

  return `all ${transitionDurationString} ${transition}`;
};

const withDefault = (value, defaultValue) => {
  if (value === undefined) {
    return defaultValue;
  }

  return value;
};

export function ContainerElement(props: ContainerElementProps) {
  // remove isLive from rest props since we're spreading it on the div (which is frowned upon)
  const { element, children, attributes, className, isLive: _isLive, ...rest } = props;
  const {
    width,
    width_max,
    height,
    keep_aspect_ratio,
    horizontalAlign,
    verticalAlign,
    backgroundColor,
    blur,
    backgroundDarken,
    backgroundVideo,
    mobile_hide,
    desktop_hide,
    style,
    url,
    gap,
    flexBasis,
    open_in_new_tab,
    internal_link,
    animation,
    padding,
    mobilePadding,
    margin,
    overflow = 'visible',
    borderRadius,
    borderColor,
    borderWidth,
    borderStyle,
    mobileBorderWidth,
    mobileBorderColor,
    mobileBorderStyle,
    mobileBorderRadius,
    shadow,
    customAttributes,
    backgroundImage: elBackgroundImage,
    flexDirection = 'column',
    mobileFlexDirection,
    hoverShadow,
    hoverBackgroundColor,
    hoverBorderColor,
    hoverScale,
    hoverBorderRadius,
    hoverBorderWidth,
    hoverBorderStyle,
    transitionDuration = 0.3,
    transition = 'none',
    flexWrap = 'nowrap',
    position,
    top,
    right,
    bottom,
    left,
    zIndex,
    positionTopActive,
    positionRightActive,
    positionBottomActive,
    positionLeftActive,
    centerHorizontally,
    centerVertically,
    mobilePosition,
    mobileTop,
    mobileRight,
    mobileBottom,
    mobileLeft,
    mobileZIndex,
    mobilePositionTopActive,
    mobilePositionRightActive,
    mobilePositionBottomActive,
    mobilePositionLeftActive,
    mobileCenterHorizontally,
    mobileCenterVertically,
    mobileHorizontalAlign,
    mobileGap,
  } = element.attrs || {};

  let bgImage: any;
  if (elBackgroundImage) {
    bgImage = <BackgroundImage src={elBackgroundImage} darken={backgroundDarken} />;
  }

  const [marginTop, marginRight, marginBottom, marginLeft] = (margin ?? CONTAINER_DEFAULTS.margin)
    .toString()
    .split(' ');
  const [animatedRef, animatedClassname] = useAnimatedBlock(animation);

  const withLinkWrapper = !!url;

  /* spreading props on standard DOM elements causes React warnings and hydration errors */
  return (
    <div
      {...rest}
      ref={animatedRef}
      role="presentation"
      {...customAttributes}
      className={classNames(
        withLinkWrapper ? styles.linkWrapper : undefined,
        {
          'mobile:hidden': mobile_hide,
          'desktop:hidden': desktop_hide,
          [styles.alignLeft]: horizontalAlign === 'left',
          [styles.alignCenter]: horizontalAlign === 'center',
          [styles.alignRight]: horizontalAlign === 'right',
          [styles.alignTop]: verticalAlign === 'align_start',
          [styles.alignMiddle]: verticalAlign === 'align_center',
          [styles.alignBottom]: verticalAlign === 'align_end',
          [styles.justifyContentSpaceBetween]: verticalAlign === 'space_between',
          [styles.justifyContentSpaceAround]: verticalAlign === 'space_around',
          [styles.justifyContentSpaceEvenly]: verticalAlign === 'space_evenly',
          card: style === 'card',
          [styles.flexDirectionColumn]: flexDirection === 'column',
          [styles.flexDirectionRow]: flexDirection === 'row',
          [styles.heightFill]: height === 'stretch',
          [styles.mobileHorizontalAlignLeft]: mobileHorizontalAlign === 'left',
          [styles.mobileHorizontalAlignCenter]: mobileHorizontalAlign === 'center',
          [styles.mobileHorizontalAlignRight]: mobileHorizontalAlign === 'right',
        },
        className,
        animatedClassname,
        styles.containerElement,
        customAttributes?.className,
      )}
      style={
        {
          ...generatePositionStyle({
            position: position || POSITION_DEFAULTS.position,
            top: top || POSITION_DEFAULTS.positionTop,
            right: right || POSITION_DEFAULTS.positionRight,
            bottom: bottom || POSITION_DEFAULTS.positionBottom,
            left: left || POSITION_DEFAULTS.positionLeft,
            zIndex: zIndex || POSITION_DEFAULTS.zIndex,
            positionTopActive: withDefault(positionTopActive, POSITION_DEFAULTS.positionTopActive),
            positionRightActive: withDefault(positionRightActive, POSITION_DEFAULTS.positionRightActive),
            positionBottomActive: withDefault(positionBottomActive, POSITION_DEFAULTS.positionBottomActive),
            positionLeftActive: withDefault(positionLeftActive, POSITION_DEFAULTS.positionLeftActive),
            centerHorizontally: withDefault(centerHorizontally, POSITION_DEFAULTS.centerHorizontally),
            centerVertically: withDefault(centerVertically, POSITION_DEFAULTS.centerVertically),
          }),
          ...generatePositionStyle({
            position: mobilePosition || position || POSITION_DEFAULTS.position,
            top: mobileTop || top || POSITION_DEFAULTS.positionTop,
            right: mobileRight || right || POSITION_DEFAULTS.positionRight,
            bottom: mobileBottom || bottom || POSITION_DEFAULTS.positionBottom,
            left: mobileLeft || left || POSITION_DEFAULTS.positionLeft,
            zIndex: mobileZIndex || zIndex || POSITION_DEFAULTS.zIndex,
            positionTopActive: withDefault(
              mobilePositionTopActive,
              positionTopActive || POSITION_DEFAULTS.positionTopActive,
            ),
            positionRightActive: withDefault(
              mobilePositionRightActive,
              positionRightActive || POSITION_DEFAULTS.positionRightActive,
            ),
            positionBottomActive: withDefault(
              mobilePositionBottomActive,
              positionBottomActive || POSITION_DEFAULTS.positionBottomActive,
            ),
            positionLeftActive: withDefault(
              mobilePositionLeftActive,
              positionLeftActive || POSITION_DEFAULTS.positionLeftActive,
            ),
            centerHorizontally: withDefault(
              mobileCenterHorizontally,
              centerHorizontally || POSITION_DEFAULTS.centerHorizontally,
            ),
            centerVertically: withDefault(
              mobileCenterVertically,
              centerVertically || POSITION_DEFAULTS.centerVertically,
            ),
            prefix: 'mobile',
          }),
          '--flex-basis': flexBasis || 'initial',
          '--padding': padding ?? CONTAINER_DEFAULTS.padding,
          '--mobile-padding': mobilePadding ?? padding ?? CONTAINER_DEFAULTS.padding,
          '--margin-top': marginTop,
          '--margin-right': marginRight,
          '--margin-bottom': marginBottom,
          '--margin-left': marginLeft,
          '--margin': margin ?? CONTAINER_DEFAULTS.margin,
          '--min-height': getHeightStyle(height).height,
          '--width': getWidthStyle(width, width_max).width,
          '--max-width': getWidthStyle(width, width_max).maxWidth,
          '--overflow': overflow ?? CONTAINER_DEFAULTS.overflow,
          '--blur': blur,
          '--gap': gap ?? 0,
          '--mobile-gap': mobileGap || gap || 0,
          '--flex-direction': flexDirection,
          '--mobile-flex-direction': mobileFlexDirection || flexDirection,
          '--mobile-border-width': mobileBorderWidth || borderWidth,
          '--mobile-border-color': mobileBorderColor || borderColor,
          '--mobile-border-style': mobileBorderStyle || borderStyle,
          '--mobile-border-radius': mobileBorderRadius || borderRadius,
          '--hover-shadow': hoverShadow || shadow,
          '--hover-background-color': hoverBackgroundColor || backgroundColor,
          '--hover-border-color': hoverBorderColor || borderColor,
          '--hover-border-radius': hoverBorderRadius || borderRadius,
          '--hover-border-width': hoverBorderWidth || borderWidth,
          '--hover-border-style': hoverBorderStyle || borderStyle,
          '--hover-scale': hoverScale || '1',
          '--transition': generateTransition(transitionDuration, transition),
          '--flex-wrap': flexWrap,
          ...getBackgroundColorStyle(backgroundColor),
          borderRadius: borderRadius,
          borderColor: borderColor,
          borderWidth: borderWidth,
          borderStyle: borderStyle,
          boxShadow: shadow,
          ...(position === 'absolute' && { zIndex: 1 }),
          ...(keep_aspect_ratio && width && height && { '--aspect-ratio': `${width} / ${height}` }),
        } as CSSProperties
      }
      {...attributes}
    >
      {isValidElement(bgImage) && (
        <div contentEditable={false} className={styles.backgroundImage}>
          {bgImage}
        </div>
      )}
      {backgroundVideo && (
        <video
          // ref={videoRef}
          contentEditable={false}
          className={classNames(styles.backgroundVideo, {
            card: style === 'card',
          })}
          style={getBackgroundDarkenStyle(backgroundDarken)}
          autoPlay
          muted
          loop
          playsInline
        >
          <source src={backgroundVideo} />
        </video>
      )}
      {children}
      {url && (
        <Link href={url} internal_link={internal_link} open_in_new_tab={open_in_new_tab} className={styles.link} />
      )}
    </div>
  );
}
